import React from "react";
import { cn } from "../../general";

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div className={cn("animate-pulse rounded-md bg-gray-200", className)} />
);

interface FieldSkeletonProps {
  className?: string;
}

const FieldSkeleton: React.FC<FieldSkeletonProps> = ({ className }) => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <Skeleton className="h-4 w-20" />
    <Skeleton className="h-11 w-full rounded-lg" />
  </div>
);

interface UserDetailsViewSkeletonProps {
  className?: string;
}

const UserDetailsViewSkeleton: React.FC<UserDetailsViewSkeletonProps> = ({
  className,
}) => {
  return (
    <div className={cn("min-h-screen", className)}>
      {/* Header Section */}
      <div className="border-b border-[#E9EAEB] bg-white py-4">
        <div className="mx-auto flex w-full max-w-none px-4 sm:px-6 lg:max-w-7xl xl:max-w-none xl:px-8 flex-col gap-4">
          {/* Breadcrumb skeleton */}
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-20" />
          </div>

          {/* Header title and subtitle */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <Skeleton className="h-6 sm:h-8 w-32 sm:w-48 mb-2" />
              <Skeleton className="h-4 w-24 sm:w-32" />
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="mx-auto w-full max-w-none px-4 sm:px-6 lg:max-w-7xl xl:max-w-none xl:px-8 py-4 sm:py-8">
        <div className="space-y-6">
          {/* Account Details Section */}
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            <div className="lg:w-48 flex-shrink-0">
              <Skeleton className="h-5 w-28 mb-4 lg:mb-0" />
            </div>
            <div className="flex-1 rounded-xl border border-[#E9EAEB] bg-white shadow-sm">
              <div className="px-4 sm:px-6 py-6">
                <div className="grid gap-4 sm:gap-6">
                  {/* First and Last Name Row */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FieldSkeleton />
                    <FieldSkeleton />
                  </div>

                  {/* Email and Phone Row */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FieldSkeleton />
                    <FieldSkeleton />
                  </div>

                  {/* Status Field */}
                  <FieldSkeleton />

                  {/* Date Created and Last Login Row */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FieldSkeleton />
                    <FieldSkeleton />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Access Settings Section */}
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            <div className="lg:w-48 flex-shrink-0">
              <Skeleton className="h-5 w-32 mb-4 lg:mb-0" />
            </div>
            <div className="flex-1 rounded-xl border border-[#E9EAEB] bg-white shadow-sm">
              <div className="px-4 sm:px-6 py-6">
                <div className="grid grid-cols-1 gap-4">
                  {/* Role Field */}
                  <FieldSkeleton />
                  {/* Branch Field */}
                  <FieldSkeleton />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsViewSkeleton;
